/**
 * Persist which trainable models are "in configurator" per integration.
 * Used by Profile → Models tab and Connector configurator.
 */

import { GPT5_CONFIGURATOR_MODEL_IDS } from './gpt5ConfiguratorModels';

const STORAGE_KEY_PREFIX = 'connector_configurator_models_';
const STORAGE_KEY_LABELS_PREFIX = 'connector_configurator_model_labels_';

export function getConfiguratorModelIds(integrationId: string): string[] {
  if (!integrationId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + integrationId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setConfiguratorModelIds(integrationId: string, modelIds: string[]): void {
  if (!integrationId) return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + integrationId, JSON.stringify(modelIds));
  } catch {
    // ignore
  }
}

/**
 * Human-readable labels for configurator model ids, keyed by technical id
 * (e.g. an OpenAI `ft:…` id → the friendly model name chosen at fine-tuning).
 */
export function getConfiguratorModelLabels(integrationId: string): Record<string, string> {
  if (!integrationId) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LABELS_PREFIX + integrationId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function setConfiguratorModelLabels(integrationId: string, labels: Record<string, string>): void {
  if (!integrationId) return;
  try {
    localStorage.setItem(STORAGE_KEY_LABELS_PREFIX + integrationId, JSON.stringify(labels));
  } catch {
    // ignore
  }
}

export function toggleConfiguratorModel(
  integrationId: string,
  modelId: string,
  enabled: boolean,
  label?: string
): void {
  const current = getConfiguratorModelIds(integrationId);
  const set = new Set(current);
  if (enabled) set.add(modelId);
  else set.delete(modelId);
  setConfiguratorModelIds(integrationId, Array.from(set));

  const labels = getConfiguratorModelLabels(integrationId);
  const cleanLabel = (label || '').trim();
  if (enabled && cleanLabel && cleanLabel !== modelId) {
    labels[modelId] = cleanLabel;
  } else if (!enabled) {
    delete labels[modelId];
  }
  setConfiguratorModelLabels(integrationId, labels);
}

/** Prettifies a raw OpenAI fine-tuned id (`ft:base:org:suffix:id`) when no stored label exists. */
export function prettifyModelId(id: string): string {
  const raw = (id || '').trim();
  if (!raw.toLowerCase().startsWith('ft:')) return raw;
  const parts = raw.split(':');
  const base = (parts[1] || '').trim();
  const suffix = (parts[3] || '').trim();
  if (suffix) return base ? `${suffix} · ${base}` : suffix;
  return base ? `Fine-tuned · ${base}` : raw;
}

/**
 * Display label for a configurator model id: stored friendly name if present,
 * otherwise a prettified version of the technical id (never the raw `ft:…` blob
 * when we can avoid it).
 */
export function formatConfiguratorModelLabel(id: string, labels?: Record<string, string>): string {
  const raw = (id || '').trim();
  if (!raw) return raw;
  const stored = labels?.[raw];
  if (stored && stored.trim()) return stored.trim();
  return prettifyModelId(raw);
}

/** Default base models shown in configurator when no selection is saved */
export const DEFAULT_CONFIGURATOR_MODELS = [
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-2025-04-14',
  'gpt-4.1-nano-2025-04-14',
  ...GPT5_CONFIGURATOR_MODEL_IDS,
];

/** Merge saved/API model ids with defaults; defaults always come first (incl. gpt-5.5). */
export function mergeConfiguratorModelOptions(options: string[] = []): string[] {
  const known = new Set<string>(DEFAULT_CONFIGURATOR_MODELS);
  const extras: string[] = [];
  for (const raw of options) {
    const modelId = String(raw ?? '').trim();
    if (!modelId || known.has(modelId) || extras.includes(modelId)) continue;
    extras.push(modelId);
  }
  return [...DEFAULT_CONFIGURATOR_MODELS, ...extras];
}

/**
 * List of model IDs to show in the configurator for this integration.
 * Profile → Models toggles add fine-tuned ids; base models from defaults are always included.
 */
export function getAvailableModelsForConfigurator(integrationId: string): string[] {
  return mergeConfiguratorModelOptions(getConfiguratorModelIds(integrationId));
}
